import { useState } from "react";
import { Scale } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const CATEGORIES = [
  { max: 18.5, label: "Underweight", color: "#3B82F6" },
  { max: 25, label: "Normal weight", color: "#16A34A" },
  { max: 30, label: "Overweight", color: "#F59E0B" },
  { max: Infinity, label: "Obese", color: "#DC2626" },
];
const SCALE_MIN = 15;
const SCALE_MAX = 40;
const pct = (v) => Math.min(100, Math.max(0, ((v - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100));

const BmiResult = ({ bmi }) => {
  const cat = CATEGORIES.find((c) => bmi < c.max);
  return (
    <div className="mt-8" data-testid="bmi-result">
      <p className="text-sm text-muted-foreground">Your BMI</p>
      <p className="mt-1 flex items-baseline gap-3">
        <span className="font-display text-5xl font-bold text-[#1A252C]" data-testid="bmi-value">{bmi.toFixed(1)}</span>
        <span className="font-semibold" style={{ color: cat.color }} data-testid="bmi-category">{cat.label}</span>
      </p>
      <div className="relative mt-6 h-3 rounded-full overflow-hidden flex">
        {[[SCALE_MIN, 18.5], [18.5, 25], [25, 30], [30, SCALE_MAX]].map(([a, b], i) => (
          <div key={a} style={{ width: `${pct(b) - pct(a)}%`, background: CATEGORIES[i].color }} />
        ))}
      </div>
      <div className="relative h-4">
        <span className="absolute -top-1 h-5 w-1.5 -translate-x-1/2 rounded bg-[#1A252C]" style={{ left: `${pct(bmi)}%` }} data-testid="bmi-marker" />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground"><span>15</span><span>18.5</span><span>25</span><span>30</span><span>40</span></div>
      {bmi >= 23 && (
        <p className="mt-5 rounded-xl bg-accent px-4 py-3 text-sm text-[#1A252C]" data-testid="bmi-asian-note">
          For Indian adults, a BMI of 23 or more is linked to a higher risk of diabetes, high blood pressure and heart disease. A check-up can help.
        </p>
      )}
      <Button asChild className="mt-6 rounded-full" data-testid="bmi-book-btn"><a href="#book">Book a consultation</a></Button>
    </div>
  );
};

const UnitToggle = ({ unit, setUnit }) => (
  <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 text-sm" role="group" aria-label="Height unit">
    {[["cm", "Centimetres"], ["ft", "Feet & inches"]].map(([key, text]) => (
      <button key={key} type="button" onClick={() => setUnit(key)} aria-pressed={unit === key}
        className={`rounded-full px-4 py-1.5 transition-colors ${unit === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-[#1A252C]"}`}
        data-testid={`bmi-unit-${key}`}>{text}</button>
    ))}
  </div>
);

const NO_SPIN = "pr-9 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";

const numInput = (id, placeholder, value, onChange, testId, extra = "") => (
  <Input id={id} type="number" inputMode="decimal" min="0" placeholder={placeholder} className={`h-11 bg-white ${extra}`} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
);

export const BmiCalculator = () => {
  const [unit, setUnit] = useState("cm");
  const [height, setHeight] = useState("");
  const [feet, setFeet] = useState("");
  const [inches, setInches] = useState("");
  const [weight, setWeight] = useState("");
  const isFt = unit === "ft";
  const inchNum = Number(inches || 0);
  const h = isFt ? (Number(feet) * 12 + inchNum) * 2.54 : Number(height);
  const w = Number(weight);
  const heightEntered = isFt ? feet !== "" : height !== "";
  const inchesOk = !isFt || (inchNum >= 0 && inchNum < 12);
  const valid = inchesOk && h >= 50 && h <= 250 && w >= 2 && w <= 300;
  const bmi = valid ? w / (h / 100) ** 2 : null;
  const showError = heightEntered && weight !== "" && !valid;
  const errorText = isFt
    ? "Please enter a height between 1 ft 8 in and 8 ft 2 in (inches 0–11) and a weight between 2–300 kg."
    : "Please enter a height between 50–250 cm and a weight between 2–300 kg.";

  return (
    <section id="bmi" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-sm font-semibold uppercase tracking-widest text-secondary">Health tool</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1A252C]">BMI calculator</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">Body Mass Index (BMI) is a quick check of whether your weight is healthy for your height. It is a screening guide, not a diagnosis.</p>
        </div>
        <div className="lg:col-span-7 rounded-3xl border border-slate-100 bg-background p-6 sm:p-10" data-testid="bmi-calculator">
          <UnitToggle unit={unit} setUnit={setUnit} />
          <div className="mt-5 grid sm:grid-cols-2 gap-5">
            {isFt ? (
              <div className="space-y-2">
                <Label htmlFor="bmi-feet">Height (ft &amp; in)</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">{numInput("bmi-feet", "5", feet, setFeet, "bmi-feet-input", NO_SPIN)}<span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">ft</span></div>
                  <div className="relative">{numInput("bmi-inches", "7", inches, setInches, "bmi-inches-input", NO_SPIN)}<span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">in</span></div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="bmi-height">Height (cm)</Label>
                {numInput("bmi-height", "e.g. 170", height, setHeight, "bmi-height-input")}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="bmi-weight">Weight (kg)</Label>
              {numInput("bmi-weight", "e.g. 70", weight, setWeight, "bmi-weight-input")}
            </div>
          </div>
          {showError && <p className="mt-4 text-sm text-destructive" data-testid="bmi-error">{errorText}</p>}
          {bmi ? <BmiResult bmi={bmi} /> : !showError && <p className="mt-6 flex items-center gap-2 text-sm text-muted-foreground" data-testid="bmi-hint"><Scale className="h-4 w-4" /> Enter your height and weight to see your BMI.</p>}
        </div>
      </div>
    </section>
  );
};
